/**
 * Ready gate after the task link resolves — white execute theme.
 */
import { useState } from 'react';
import AppInstallHeadBanner from '@/components/AppInstallHeadBanner';
import { useAppInstallBanner } from '@/hooks/useAppInstallBanner';
import {
  attemptSchemeHandoff,
  NATIVE_APP_HOME_SCHEME,
} from '@/services/nativeAppHandoff';
import X9Logo from '../components/X9Logo';
import StatusBadge from '../components/StatusBadge';
import type { ResolveTaskResponse } from '../types';

interface Props {
  task: ResolveTaskResponse['task'];
  onViewTask: () => void;
  /** Optional: lift banner to TaskPage so it survives View task. */
  installBanner?: ReturnType<typeof useAppInstallBanner>;
}

export default function TaskReadyStep({
  task,
  onViewTask,
  installBanner: installBannerProp,
}: Props) {
  const localBanner = useAppInstallBanner({
    softShowOnMount: !installBannerProp,
  });
  const installBanner = installBannerProp ?? localBanner;
  const [opening, setOpening] = useState(false);

  const displayStatus = task.displayStatus ?? task.status;
  const ownerViewer = task.viewerRole === 'owner';
  const claimedByOther = !ownerViewer && task.executionAvailability === 'claimed_by_other';
  const terminal = !ownerViewer && task.executionAvailability === 'terminal';
  const resumedByAssignee =
    displayStatus === 'reopened'
    || (task.executionAvailability === 'claimed_by_me' && task.status === 'pending');

  const handleOpenInApp = async () => {
    if (opening) return;
    setOpening(true);
    try {
      const { launched } = await attemptSchemeHandoff(NATIVE_APP_HOME_SCHEME);
      if (!launched) {
        installBanner.showAfterFailedHandoff();
      }
    } finally {
      setOpening(false);
    }
  };

  return (
    <>
      {!installBannerProp ? (
        <AppInstallHeadBanner
          open={installBanner.visible}
          onDismiss={installBanner.dismiss}
        />
      ) : null}
      <div className="execute-screen items-center justify-center px-6 pb-[max(env(safe-area-inset-bottom),16px)] text-center">
        <div className="w-[min(25vw,5.7rem)] shrink-0 text-x9-marine">
          <X9Logo title="X9" className="block w-full select-none" />
        </div>

        <div className="mt-10 min-h-[6.5rem] max-w-sm">
          <StatusBadge status={displayStatus} surface="paper" variant="filled" className="justify-center" />
          <h1 className="mt-3 text-xl font-semibold tracking-tight text-x9-ink">Task found</h1>
          <p className="mt-2 text-sm leading-relaxed text-x9-marineSoft">
            {ownerViewer
              ? 'You assigned this task. Review it here — only the assignee can work on it.'
              : claimedByOther
                ? 'This task is already being worked on.'
                : terminal
                  ? 'This task is no longer open for execution.'
                  : resumedByAssignee
                    ? `${task.creatorName} reopened this task. Continue when you\u2019re ready.`
                    : `${task.creatorName} shared this task with you. Review it before accepting.`}
          </p>
        </div>

        <div className="mt-10 w-full max-w-md space-y-3 transition duration-300 ease-out motion-reduce:transition-none">
          <button type="button" className="btn-paper-primary w-full" onClick={onViewTask}>
            {claimedByOther || terminal ? 'View status' : 'View task'}
          </button>
          <button
            type="button"
            className="btn-paper-secondary w-full"
            onClick={() => void handleOpenInApp()}
            disabled={opening}
            aria-busy={opening}
          >
            {opening ? 'Opening…' : 'Open in X9 app'}
          </button>
        </div>
      </div>
    </>
  );
}
